import { Component, Inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { CommonService } from '../../services/common/common.service';
import { ApiUrlHelper } from '../../common/api-url-helper';

export interface DocumentPreviewItem {
  attachmentId?: number;
  fileName: string;
  fileType: string;
  fileSize?: string;
  fileUrl?: string;
}

export interface DocumentPreviewData {
  document: DocumentPreviewItem;
  attachments?: DocumentPreviewItem[];
  currentIndex?: number;
  ticketId?: string;
}

@Component({
  selector: 'app-document-preview-modal',
  standalone: false,
  templateUrl: './document-preview-modal.html',
  styleUrl: './document-preview-modal.scss'
})
export class DocumentPreviewModal implements OnInit, OnDestroy {
  activeDoc!: DocumentPreviewItem;
  attachments: DocumentPreviewItem[] = [];
  currentIndex: number = 0;
  ticketId?: string;

  zoomScale: number = 1.0;
  rotationAngle: number = 0;
  isLoading: boolean = true;
  isFullScreen: boolean = false;

  safePdfUrl: SafeResourceUrl | null = null;
  safeImageUrl: SafeUrl | null = null;
  previewUnavailable: boolean = false;

  supportedFormats = ['PDF', 'JPG', 'JPEG', 'PNG'];
  private activeBlobUrl: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<DocumentPreviewModal>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentPreviewData,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private http: HttpClient,
    private commonService: CommonService,
    private api: ApiUrlHelper
  ) {
    if (data) {
      this.activeDoc = data.document;
      this.attachments = data.attachments && data.attachments.length > 0 ? data.attachments : [data.document];
      this.currentIndex = data.currentIndex !== undefined && data.currentIndex >= 0 ? data.currentIndex : 0;
      this.ticketId = data.ticketId;
    }
  }

  ngOnInit(): void {
    this.loadActiveDocument();
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrl();
  }

  private cleanupBlobUrl(): void {
    if (this.activeBlobUrl) {
      URL.revokeObjectURL(this.activeBlobUrl);
      this.activeBlobUrl = null;
    }
  }

  get normalizedFormat(): string {
    if (!this.activeDoc) return '';
    let ext = this.activeDoc.fileType?.toUpperCase() || '';
    if (!ext && this.activeDoc.fileName) {
      const parts = this.activeDoc.fileName.split('.');
      if (parts.length > 1) {
        ext = parts[parts.length - 1].toUpperCase();
      }
    }
    return ext;
  }

  get isPdf(): boolean {
    return this.normalizedFormat === 'PDF';
  }

  get isImage(): boolean {
    return ['JPG', 'JPEG', 'PNG'].includes(this.normalizedFormat);
  }

  get isSupported(): boolean {
    return this.isPdf || this.isImage;
  }

  private getCleanRelativePath(urlStr: string): string {
    if (!urlStr) return '';
    let path = urlStr;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      try {
        const parsed = new URL(urlStr);
        path = parsed.pathname;
      } catch {
        // Fallback if URL parsing fails
      }
    }
    while (path.startsWith('/')) {
      path = path.substring(1);
    }
    const supportIndex = path.indexOf('SupportTickets/');
    if (supportIndex >= 0) {
      path = path.substring(supportIndex);
    }
    return path;
  }

  private getDirectStaticUrl(rawUrl: string, cleanPath: string): string {
    if (!rawUrl) return '';
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      try {
        const parsed = new URL(rawUrl);
        const supportIdx = parsed.pathname.indexOf('SupportTickets/');
        if (supportIdx >= 0) {
          return `${parsed.origin}/${parsed.pathname.substring(supportIdx)}`;
        }
        return rawUrl;
      } catch {
        return rawUrl;
      }
    }
    let rootApiUrl = environment.TemplateUrl || environment.BaseURL || '';
    try {
      const u = new URL(rootApiUrl);
      rootApiUrl = u.origin + '/';
    } catch {
      if (!rootApiUrl.endsWith('/')) rootApiUrl += '/';
    }
    return rootApiUrl + cleanPath;
  }

  loadActiveDocument(): void {
    this.cleanupBlobUrl();
    this.isLoading = true;
    this.zoomScale = 1.0;
    this.rotationAngle = 0;
    this.safePdfUrl = null;
    this.safeImageUrl = null;
    this.previewUnavailable = false;

    if (!this.activeDoc) {
      this.isLoading = false;
      return;
    }

    if (!this.activeDoc.fileUrl && !this.activeDoc.attachmentId) {
      this.isLoading = false;
      this.previewUnavailable = true;
      return;
    }

    const rawUrl = this.activeDoc.fileUrl || '';
    const cleanPath = this.getCleanRelativePath(rawUrl);
    const directUrl = this.getDirectStaticUrl(rawUrl, cleanPath);

    if (this.isPdf) {
      const attachmentId = this.activeDoc.attachmentId;
      if (attachmentId) {
        const apiUrl = this.api.apiUrl.SupportTicket.DownloadAttachment.replace('{attachmentId}', attachmentId.toString());
        this.commonService.downloadFileGet(apiUrl).subscribe({
          next: (blob) => {
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            this.activeBlobUrl = URL.createObjectURL(pdfBlob);
            this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.activeBlobUrl);
            this.isLoading = false;
            this.previewUnavailable = false;
          },
          error: () => {
            if (directUrl) {
              this.http.get(directUrl, { responseType: 'blob' }).subscribe({
                next: (blob) => {
                  const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                  this.activeBlobUrl = URL.createObjectURL(pdfBlob);
                  this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.activeBlobUrl);
                  this.isLoading = false;
                  this.previewUnavailable = false;
                },
                error: () => {
                  this.isLoading = false;
                  this.previewUnavailable = true;
                }
              });
            } else {
              this.isLoading = false;
              this.previewUnavailable = true;
            }
          }
        });
      } else if (directUrl) {
        this.http.get(directUrl, { responseType: 'blob' }).subscribe({
          next: (blob) => {
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            this.activeBlobUrl = URL.createObjectURL(pdfBlob);
            this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.activeBlobUrl);
            this.isLoading = false;
            this.previewUnavailable = false;
          },
          error: () => {
            this.isLoading = false;
            this.previewUnavailable = true;
          }
        });
      } else {
        this.isLoading = false;
        this.previewUnavailable = true;
      }
    } else if (this.isImage) {
      this.safeImageUrl = this.sanitizer.bypassSecurityTrustUrl(directUrl);
      this.isLoading = false;
      this.previewUnavailable = false;
    } else {
      this.isLoading = false;
    }
  }

  onImageLoad(): void {
    this.isLoading = false;
  }

  onImageError(): void {
    this.isLoading = false;
    this.previewUnavailable = true;
  }

  zoomIn(): void {
    if (this.zoomScale < 3.0) {
      this.zoomScale = parseFloat((this.zoomScale + 0.25).toFixed(2));
    }
  }

  zoomOut(): void {
    if (this.zoomScale > 0.5) {
      this.zoomScale = parseFloat((this.zoomScale - 0.25).toFixed(2));
    }
  }

  resetZoom(): void {
    this.zoomScale = 1.0;
    this.rotationAngle = 0;
  }

  rotateLeft(): void {
    this.rotationAngle = (this.rotationAngle - 90 + 360) % 360;
  }

  rotateRight(): void {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
  }

  toggleFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
  }

  prevDoc(): void {
    if (this.hasPrev) {
      this.currentIndex--;
      this.activeDoc = this.attachments[this.currentIndex];
      this.loadActiveDocument();
    }
  }

  nextDoc(): void {
    if (this.hasNext) {
      this.currentIndex++;
      this.activeDoc = this.attachments[this.currentIndex];
      this.loadActiveDocument();
    }
  }

  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.attachments.length - 1;
  }

  close(): void {
    this.dialogRef.close();
  }

  downloadDocument(): void {
    const fileName = this.activeDoc?.fileName;

    if (this.activeBlobUrl) {
      const anchor = document.createElement('a');
      anchor.href = this.activeBlobUrl;
      anchor.download = fileName;
      anchor.click();
      return;
    }

    const attachmentId = this.activeDoc?.attachmentId;
    if (attachmentId) {
      const apiUrl = this.api.apiUrl.SupportTicket.DownloadAttachment.replace('{attachmentId}', attachmentId.toString());
      this.commonService.downloadFileGet(apiUrl).subscribe({
        next: (blob) => {
          const blobUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.download = fileName;
          anchor.click();
          URL.revokeObjectURL(blobUrl);
        },
        error: () => {
          this.toastr.error(`Failed to download ${fileName}.`);
        }
      });
      return;
    }

    const rawUrl = this.activeDoc?.fileUrl;
    if (rawUrl) {
      const cleanPath = this.getCleanRelativePath(rawUrl);
      const directUrl = this.getDirectStaticUrl(rawUrl, cleanPath);
      const anchor = document.createElement('a');
      anchor.href = directUrl;
      anchor.download = fileName;
      anchor.target = '_blank';
      anchor.click();
      return;
    }

    this.toastr.error('Attachment is not available for download.');
  }

  openInNewTab(): void {
    if (this.isPdf && this.activeBlobUrl) {
      window.open(this.activeBlobUrl, '_blank');
      return;
    }
    const rawUrl = this.activeDoc?.fileUrl;
    if (rawUrl) {
      const cleanPath = this.getCleanRelativePath(rawUrl);
      const directUrl = this.getDirectStaticUrl(rawUrl, cleanPath);
      window.open(directUrl, '_blank');
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      this.prevDoc();
    } else if (event.key === 'ArrowRight') {
      this.nextDoc();
    } else if (event.key === 'Escape') {
      this.close();
    } else if (event.key === '+' || event.key === '=') {
      this.zoomIn();
    } else if (event.key === '-') {
      this.zoomOut();
    }
  }

}
